/**
 * Data nominal — vencimento, competência, emissão (spec 01 §8.2).
 *
 * Uma data nominal não é um instante: "vence em 20/07" vence em 20/07 em qualquer
 * lugar do mundo. Representá-la como `Date` (que é um instante em UTC) é o defeito
 * clássico que faz um vencimento aparecer no dia 19 para quem abre o sistema de
 * outro fuso — e, pior, faz uma competência cair no mês errado na virada.
 *
 * Por isso o tipo aqui é `string 'YYYY-MM-DD'` marcada, e toda a aritmética é feita
 * sobre os inteiros ano/mês/dia. **Nenhuma função deste arquivo constrói um `Date`
 * com fuso** — a única que toca em fuso é `deInstanteUTC`, que é justamente a porta
 * de entrada do §8.2 ("data vinda de API externa em UTC é convertida para
 * America/Sao_Paulo ANTES de virar competência").
 */

declare const marcaDataNominal: unique symbol

/** String 'YYYY-MM-DD' já validada. A marca impede passar uma string qualquer. */
export type DataNominal = string & { readonly [marcaDataNominal]: true }

/** 'YYYY-MM' — competência. */
export type Competencia = string & { readonly [marcaDataNominal]: 'competencia' }

export class DataNominalInvalida extends Error {
  constructor(entrada: unknown) {
    super(`data nominal inválida (esperado 'YYYY-MM-DD'): ${JSON.stringify(entrada)}`)
    this.name = 'DataNominalInvalida'
  }
}

const FUSO_DA_OPERACAO = 'America/Sao_Paulo'

function bissexto(ano: number): boolean {
  return (ano % 4 === 0 && ano % 100 !== 0) || ano % 400 === 0
}

const DIAS_POR_MES = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

export function diasNoMes(ano: number, mes: number): number {
  if (mes === 2 && bissexto(ano)) return 29
  return DIAS_POR_MES[mes - 1] ?? 0
}

interface Partes {
  ano: number
  mes: number
  dia: number
}

function partes(d: DataNominal): Partes {
  return {
    ano: Number(d.slice(0, 4)),
    mes: Number(d.slice(5, 7)),
    dia: Number(d.slice(8, 10)),
  }
}

function montar({ ano, mes, dia }: Partes): DataNominal {
  const p = (n: number, largura: number): string => String(n).padStart(largura, '0')
  return `${p(ano, 4)}-${p(mes, 2)}-${p(dia, 2)}` as DataNominal
}

export function ehDataNominal(valor: unknown): valor is DataNominal {
  if (typeof valor !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(valor)) return false
  const ano = Number(valor.slice(0, 4))
  const mes = Number(valor.slice(5, 7))
  const dia = Number(valor.slice(8, 10))
  if (mes < 1 || mes > 12) return false
  return dia >= 1 && dia <= diasNoMes(ano, mes)
}

/** Porta de entrada: valida e marca. Data que não existe no calendário é recusada. */
export function dataNominal(valor: string): DataNominal {
  if (!ehDataNominal(valor)) throw new DataNominalInvalida(valor)
  return valor
}

/** Ordenação: em ISO, comparar as strings é comparar as datas. */
export function compararDatas(a: DataNominal, b: DataNominal): number {
  return a < b ? -1 : a > b ? 1 : 0
}

export function antesDe(a: DataNominal, b: DataNominal): boolean {
  return a < b
}

export function depoisDe(a: DataNominal, b: DataNominal): boolean {
  return a > b
}

/** Dias desde 1970-01-01, sem `Date`: contagem própria sobre o calendário gregoriano. */
function paraDiaAbsoluto(d: DataNominal): number {
  const { ano, mes, dia } = partes(d)
  // algoritmo de dias julianos (Howard Hinnant), com aritmética inteira apenas
  const a = mes <= 2 ? ano - 1 : ano
  const era = Math.floor(a / 400)
  const anoDaEra = a - era * 400
  const diaDoAno = Math.floor((153 * (mes + (mes > 2 ? -3 : 9)) + 2) / 5) + dia - 1
  const diaDaEra = anoDaEra * 365 + Math.floor(anoDaEra / 4) - Math.floor(anoDaEra / 100) + diaDoAno
  return era * 146097 + diaDaEra - 719468
}

function deDiaAbsoluto(dias: number): DataNominal {
  const z = dias + 719468
  const era = Math.floor(z / 146097)
  const diaDaEra = z - era * 146097
  const anoDaEra = Math.floor(
    (diaDaEra - Math.floor(diaDaEra / 1460) + Math.floor(diaDaEra / 36524) - Math.floor(diaDaEra / 146096)) / 365,
  )
  const ano = anoDaEra + era * 400
  const diaDoAno = diaDaEra - (365 * anoDaEra + Math.floor(anoDaEra / 4) - Math.floor(anoDaEra / 100))
  const mp = Math.floor((5 * diaDoAno + 2) / 153)
  const dia = diaDoAno - Math.floor((153 * mp + 2) / 5) + 1
  const mes = mp + (mp < 10 ? 3 : -9)
  return montar({ ano: mes <= 2 ? ano + 1 : ano, mes, dia })
}

export function somarDias(d: DataNominal, dias: number): DataNominal {
  return deDiaAbsoluto(paraDiaAbsoluto(d) + dias)
}

export function diasEntre(de: DataNominal, ate: DataNominal): number {
  return paraDiaAbsoluto(ate) - paraDiaAbsoluto(de)
}

/**
 * Soma meses **grampeando no fim do mês**: 31/01 + 1 mês = 28/02 (ou 29 em ano
 * bissexto). É o que a agenda de parcelas precisa — a alternativa (transbordar para
 * 03/03) faria um vencimento pular de mês, e o mês do vencimento é competência.
 */
export function somarMeses(d: DataNominal, meses: number): DataNominal {
  const { ano, mes, dia } = partes(d)
  const totalMeses = ano * 12 + (mes - 1) + meses
  const novoAno = Math.floor(totalMeses / 12)
  const novoMes = totalMeses - novoAno * 12 + 1
  const limite = diasNoMes(novoAno, novoMes)
  return montar({ ano: novoAno, mes: novoMes, dia: dia > limite ? limite : dia })
}

export function primeiroDiaDoMes(d: DataNominal): DataNominal {
  const { ano, mes } = partes(d)
  return montar({ ano, mes, dia: 1 })
}

export function ultimoDiaDoMes(d: DataNominal): DataNominal {
  const { ano, mes } = partes(d)
  return montar({ ano, mes, dia: diasNoMes(ano, mes) })
}

/** 'YYYY-MM' — o eixo de competência da DRE (D-13: mês de emissão da NF). */
export function competenciaDe(d: DataNominal): Competencia {
  return d.slice(0, 7) as Competencia
}

/**
 * Instante UTC → data nominal no fuso da operação (spec 01 §8.2).
 *
 * `Intl.DateTimeFormat` com `sv-SE` devolve exatamente 'YYYY-MM-DD' — usar o
 * formatador em vez de aritmética de fuso à mão é o que faz o horário de verão (e o
 * fim dele) não virar problema nosso.
 *
 * É a ÚNICA função deste arquivo que conhece fuso, e é de propósito: a conversão
 * acontece na borda, uma vez, e daí para dentro só existe data nominal.
 */
export function deInstanteUTC(instanteISO: string, fuso: string = FUSO_DA_OPERACAO): DataNominal {
  const instante = new Date(instanteISO)
  if (Number.isNaN(instante.getTime())) throw new DataNominalInvalida(instanteISO)
  const formatado = new Intl.DateTimeFormat('sv-SE', {
    timeZone: fuso,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instante)
  return dataNominal(formatado)
}
